import { useMemo } from 'react';
import { Group, Paper, Stack, Text } from '@/components/ui';
import { DonutChart } from '@mantine/charts';
import { IconChartPie } from '@tabler/icons-react';
import type { ProjectModelView } from '@actschurch/ccms-sdk';
import { computeHealthStatus } from '../../utils/projectHelpers';

interface ProjectHealthDonutProps {
  projects: ProjectModelView[];
  size?: number;
}

const HEALTH_ORDER = ['On Track', 'At Risk', 'Critical', 'Completed'] as const;
const HEALTH_COLOR: Record<(typeof HEALTH_ORDER)[number], string> = {
  'On Track': 'blue.6',
  'At Risk': 'yellow.6',
  Critical: 'red.6',
  Completed: 'teal.6',
};

export function ProjectHealthDonut({ projects, size = 200 }: ProjectHealthDonutProps) {
  const data = useMemo(() => {
    const counts: Record<string, number> = {
      'On Track': 0,
      'At Risk': 0,
      Critical: 0,
      Completed: 0,
    };
    projects
      .filter((p) => !p.isArchived)
      .forEach((p) => {
        const h = computeHealthStatus(p);
        if (h in counts) counts[h]++;
      });
    return HEALTH_ORDER.map((label) => ({
      name: label,
      value: counts[label],
      color: HEALTH_COLOR[label],
    }));
  }, [projects]);

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <Paper withBorder p="md" radius="md" h="100%">
      <Group gap="xs" mb="sm">
        <IconChartPie size={18} />
        <Text fw={600}>Health Distribution</Text>
      </Group>
      {total === 0 ? (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          No projects
        </Text>
      ) : (
        <Group justify="center" align="center" gap="lg">
          <DonutChart
            data={data}
            size={size}
            thickness={28}
            withLabelsLine
            withLabels
            chartLabel={`${total} total`}
            paddingAngle={2}
          />
          <Stack gap="xs">
            {data.map((d) => (
              <Group key={d.name} gap="xs">
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    backgroundColor: `var(--mantine-color-${d.color.replace('.', '-')})`,
                  }}
                />
                <Text size="sm">
                  {d.name} · <b>{d.value}</b>
                </Text>
              </Group>
            ))}
          </Stack>
        </Group>
      )}
    </Paper>
  );
}
